import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ChannelWithMembership } from '@avslack/shared';
import { useChannels, useToggleMembershipFlag } from '../data/channels';
import { belongsToTeam, useTeams } from '../data/teams';
import { Icon } from './Icon';

/**
 * Beallitasok > CSATORNAK: az osszes sajat csatorna egy helyen.
 *
 *  - AKTIV csatornak: a szem ikonnal donti el a felhasznalo, mi latszik az
 *    oldalsavban. Az elrejtett csatornabol csak a szemelyes @emlites
 *    ertesit, es az olvasatlan-jelzesekbe sem szamit (szabaly a szerveren:
 *    `decideNotification`).
 *  - ARCHIVALT csatornak: az oldalsavbol eltunnek, innen nyithatok meg
 *    olvasasra - a korabbi beszelgetes nem vesz el.
 *
 * A kapcsolo AZONNAL hat, nem a dialogus Mentes gombjara: egy listanyi
 * szemikon mellett a "meg el kell menteni" konnyen elfelejtodne.
 *
 * DM-ek nincsenek benne: azok emberek, nem csatornak.
 */
export function ChannelListSettings({ onOpenChannel }: { onOpenChannel: () => void }) {
  const { data: channels } = useChannels();
  const { data: teams } = useTeams();
  const toggle = useToggleMembershipFlag();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const needle = query.trim().toLocaleLowerCase('hu');

  const { active, archived } = useMemo(() => {
    const own = (channels ?? [])
      .filter((c) => (c.kind === 'public' || c.kind === 'private') && c.membership.isMember)
      .filter((c) => !needle || (c.name ?? '').toLocaleLowerCase('hu').includes(needle))
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'hu'));
    return {
      active: own.filter((c) => !c.isArchived),
      archived: own.filter((c) => c.isArchived),
    };
  }, [channels, needle]);

  // Tobb cegnel cegenkent csoportositunk - kulonben ket "#altalanos" allna egymas alatt.
  const multiTeam = (teams?.length ?? 0) > 1;
  const groupByTeam = (list: ChannelWithMembership[]) =>
    multiTeam
      ? (teams ?? [])
          .map((team) => ({
            key: team.id,
            title: team.name as string | null,
            items: list.filter((c) => belongsToTeam(c, team.id)),
          }))
          .filter((group) => group.items.length > 0)
      : [{ key: 'all', title: null, items: list }];

  const hiddenCount = active.filter((c) => c.membership.isHidden).length;

  const open = (channelId: string) => {
    onOpenChannel();
    void navigate({ to: '/c/$channelId', params: { channelId } });
  };

  const setHidden = (channel: ChannelWithMembership, isHidden: boolean) =>
    toggle.mutate({ channelId: channel.id, patch: { isHidden } });

  return (
    <div className="space-y-5">
      <section>
        <h3 className="mb-1 font-display text-sm font-semibold">Mi látszik a csatornalistában?</h3>
        <p className="mb-3 text-xs text-ink-muted">
          Az áthúzott szemmel jelölt csatorna eltűnik a bal oldali listából, és az olvasatlan-számba
          sem számít bele. Értesítést belőle <strong>csak akkor kapsz, ha személyesen</strong>{' '}
          említenek (@neved) — a @channel, a csoport-említés, a kulcsszó és a hívás sem szól. A
          csatornának továbbra is tagja maradsz, és a gyorskeresőből (Ctrl+K) is megnyithatod.
        </p>

        <label className="mb-3 flex items-center gap-2 rounded-control border border-border bg-surface px-3 py-2 focus-within:border-accent">
          <Icon name="keres" className="text-ink-faint" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Csatorna keresése"
            aria-label="Csatorna keresése"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-ink-faint"
          />
        </label>

        {active.length === 0 ? (
          <p className="text-xs text-ink-muted">
            {needle
              ? 'Nincs ilyen nevű aktív csatornád.'
              : 'Még nem vagy tagja egy csatornának sem.'}
          </p>
        ) : (
          <>
            <p className="mb-1.5 text-xs text-ink-faint" role="status">
              {active.length - hiddenCount} látszik
              {hiddenCount > 0 ? `, ${hiddenCount} elrejtve` : ''}
            </p>
            {groupByTeam(active).map((group) => (
              <div key={group.key} className="mb-3">
                {group.title && (
                  <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-faint">
                    {group.title}
                  </h4>
                )}
                <ul className="divide-y divide-border-soft rounded-card border border-border-soft">
                  {group.items.map((channel) => (
                    <ActiveRow
                      key={channel.id}
                      channel={channel}
                      pending={toggle.isPending && toggle.variables?.channelId === channel.id}
                      onOpen={() => open(channel.id)}
                      onToggle={() => setHidden(channel, !channel.membership.isHidden)}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </>
        )}

        {toggle.isError && (
          <p role="alert" className="mt-1 text-xs text-danger">
            Nem sikerült menteni. Próbáld újra.
          </p>
        )}
      </section>

      <section className="border-t border-border-soft pt-4">
        <h3 className="mb-1 flex items-center gap-2 font-display text-sm font-semibold">
          Archivált csatornák
          {archived.length > 0 && (
            <span className="rounded-pill bg-surface-sunken px-1.5 py-px text-[10px] font-semibold text-ink-muted">
              {archived.length}
            </span>
          )}
        </h3>
        <p className="mb-3 text-xs text-ink-muted">
          Az archivált csatorna nem látszik a listában, és nem lehet bele írni. A korábbi
          beszélgetést innen olvashatod vissza.
        </p>

        {archived.length === 0 ? (
          <p className="text-xs text-ink-faint">
            {needle ? 'Nincs ilyen nevű archivált csatornád.' : 'Nincs archivált csatornád.'}
          </p>
        ) : (
          groupByTeam(archived).map((group) => (
            <div key={group.key} className="mb-3">
              {group.title && (
                <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-faint">
                  {group.title}
                </h4>
              )}
              <ul className="divide-y divide-border-soft rounded-card border border-border-soft">
                {group.items.map((channel) => (
                  <li key={channel.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                    <Icon name="archivum" className="shrink-0 text-ink-faint" />
                    <span className="min-w-0 flex-1 truncate text-ink-muted">{channel.name}</span>
                    <button
                      type="button"
                      onClick={() => open(channel.id)}
                      className="flex shrink-0 items-center gap-1.5 rounded-control px-2 py-1 text-xs font-semibold text-accent transition-colors hover:bg-accent-soft"
                    >
                      <Icon name="megnyitas" size="sm" />
                      Megnyitás
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </section>
    </div>
  );
}

function ActiveRow({
  channel,
  pending,
  onOpen,
  onToggle,
}: {
  channel: ChannelWithMembership;
  pending: boolean;
  onOpen: () => void;
  onToggle: () => void;
}) {
  const hidden = channel.membership.isHidden;
  const label = hidden
    ? `#${channel.name} elrejtve — megjelenítés a listában`
    : `#${channel.name} látszik — elrejtés a listából`;

  return (
    <li className="flex items-center gap-2 px-3 py-1.5 text-sm">
      <Icon
        name={channel.kind === 'private' ? 'lakat' : 'csatorna'}
        className="shrink-0 text-ink-faint"
      />
      <button
        type="button"
        onClick={onOpen}
        title="Megnyitás"
        className={[
          'min-w-0 flex-1 truncate text-left hover:underline',
          hidden ? 'text-ink-faint line-through decoration-ink-faint/50' : '',
        ].join(' ')}
      >
        {channel.name}
      </button>
      {channel.membership.isMuted && !hidden && (
        <span className="shrink-0 rounded-pill bg-surface-sunken px-1.5 py-px text-[10px] font-semibold text-ink-muted">
          némítva
        </span>
      )}
      <button
        type="button"
        onClick={onToggle}
        disabled={pending}
        aria-pressed={!hidden}
        aria-label={label}
        title={label}
        className={[
          'grid size-8 shrink-0 place-items-center rounded-control transition-colors duration-(--duration-quick) disabled:opacity-50',
          hidden
            ? 'text-ink-faint hover:bg-surface-hover hover:text-ink'
            : 'text-accent hover:bg-accent-soft',
        ].join(' ')}
      >
        <Icon name={hidden ? 'rejt' : 'mutat'} />
      </button>
    </li>
  );
}
